<template>
  <div class="ls-review--tipping">
    <BField v-for="(number, idx) in amountsOrPercentages" :key="number">
      <BRadioButton
        v-model="tip"
        :class="{ 'with-percentage': showPercentages }"
        :test-id="`tip-button-${idx}`"
        :native-value="number"
        @input="resetOtherAndNoTip()"
      >
        <p
          :test-id="`button-main-text-${idx}`"
          :class="{
            'ls-review--tipping-percentage-text': showPercentages,
          }"
        >
          {{ getTippingOptions(number) }}
        </p>
        <p v-if="showPercentages" class="ls-review--tipping-calculated-text">
          {{ calculatedTipAmount(number) | currency }}
        </p>
      </BRadioButton>
    </BField>
    <div class="ls-review--tipping-other-no-tip-wrapper">
      <div
        :class="[
          'ls-review--tipping-other',
          {
            'ls-review--tipping-other-with-percentage': showPercentages,
            'ls-review--tipping-other-hovered': isHovered && !showOtherInput && !isMobile,
          },
        ]"
        test-id="other"
        @mouseover="isHovered = true"
        @mouseleave="isHovered = false"
        @click="clickOtherOrNoTip({ type: 'other' })"
      >
        <span v-if="!showOtherInput">Other</span>
        <LsInputMasked
          v-else
          v-model="tip"
          test-id="input"
          placeholder="0.00"
          mask="currency"
          name="review_tipping_tip"
          track-id="review_tipping-tip"
          :validation="{ required: false }"
        />
      </div>
      <div
        v-if="showPercentages"
        :class="[
          'ls-review--tipping-no-tip',
          {
            'ls-review--tipping-no-tip-with-percentage': showPercentages,
            'ls-review--tipping-no-tip-hovered': (isHoveredNoTip || noTipSelected) && !isMobile,
          },
        ]"
        test-id="no-tip"
        @mouseover="isHoveredNoTip = true"
        @mouseleave="isHoveredNoTip = false"
        @click="clickOtherOrNoTip({ type: 'no-tip' })"
      >
        <span>No Tip</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueTypes from 'vue-types';
import { LsInputMasked } from '@/components/common';
import { lsDeviceHelpers, lsFilters, lsPropTypeHelpers, lsTrackingService } from 'ls-vue-common';
import _ from 'lodash';

export default {
  name: 'ReviewTipping',
  components: { LsInputMasked },
  props: {
    trackId: VueTypes.custom(lsPropTypeHelpers.trackID).isRequired,
    tippingOptions: VueTypes.shape({
      amounts: VueTypes.array.isRequired,
      show: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
      percentages: VueTypes.oneOfType([Array, null]),
    }).isRequired,
    jobPrice: VueTypes.number.isRequired,
    value: VueTypes.oneOfType([String, Number]).isRequired,
  },
  data() {
    return {
      showOtherInput: false,
      otherAmountEntered: false,
      isHovered: false,
      isHoveredNoTip: false,
      noTipSelected: false,
      isMobile: lsDeviceHelpers.isMobile(),
    };
  },
  //TODO2: Fix issue where clicking on button when a matching value is entered in the Other input does not select that button
  computed: {
    tip: {
      get() {
        return this.value;
      },
      set(value) {
        lsTrackingService.track({
          event: 'click',
          target: this.trackId,
          data: { value },
        });
        this.$emit('input', value);
      },
    },
    showPercentages() {
      return _.get(this.tippingOptions, 'show') === 'percentage';
    },
    amountsOrPercentages() {
      const { amounts, show, percentages } = this.tippingOptions;
      if (show) {
        return show === 'amount' ? amounts : percentages;
      }
      return amounts;
    },
  },
  methods: {
    getTippingOptions(number) {
      const { show } = this.tippingOptions;

      if (!show || show === 'amount') {
        return lsFilters.currency({ amount: number, trimTrailingZeros: true });
      }
      return `${number}%`;
    },
    calculatedTipAmount(number) {
      const unRoundedTip = _.multiply(this.jobPrice, 0.01);
      const roundedTip = _.round(unRoundedTip, 2);
      return _.multiply(number, roundedTip);
    },
    clickOtherOrNoTip({ type }) {
      this.setOtherAmountFlag(false);

      // don't reset tip to '' on second click with input showing
      if (type === 'other' && !this.showOtherInput) {
        this.$emit('input', '');
      }
      if (type === 'other') {
        this.setOtherAmountFlag(true);
        this.showOtherInput = true;
        this.noTipSelected = false;
      } else if (type === 'no-tip') {
        this.$emit('input', '');
        this.showOtherInput = false;
        this.noTipSelected = !this.noTipSelected;
      }
    },
    resetOtherAndNoTip() {
      this.setOtherAmountFlag(false);
      this.showOtherInput = false;
      this.noTipSelected = false;
    },
    setOtherAmountFlag(value) {
      this.$emit('other-amount', value);
    },
  },
};
</script>

<style lang="scss">
// can't use scoped here since we are overidding buefy input

.ls-review--tipping {
  display: flex;
  .button {
    display: flex;
    flex-direction: column;
    align-content: center;
    background-color: $primary-light;
    color: $primary;
    border-radius: 0px;
    border: 0px;
    width: 45px;
    height: 45px;
    margin: 0 2px;
    &.small-button {
      height: 38px;
    }
    &:hover {
      background-color: $primary;
      color: $white;
    }
    &.is-primary {
      background-color: $primary;
      color: $white;
    }
  }
  .with-percentage {
    .button {
      width: 76px;
      height: 76px;
    }
  }
}

.ls-review--tipping-percentage-text {
  padding: 0 0 1px 2px;
  font-size: $size-3;
  font-weight: bold;
}

.ls-review--tipping-calculated-text {
  padding-top: 1px;
  font-weight: normal;
}

.ls-review--tipping-other-no-tip-wrapper {
  width: 100px;
  height: 76px;
  display: flex;
  flex-wrap: wrap;
  align-content: space-between;
}

.ls-review--tipping-other,
.ls-review--tipping-no-tip {
  background-color: $primary-light;
  color: $primary;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: $size-4;
  font-weight: bold;
  margin: 0 2px;
  user-select: none;
  width: 110px;
  height: 45px;
  padding: 0 10px;
}

.ls-review--tipping-other-with-percentage,
.ls-review--tipping-no-tip-with-percentage {
  width: 100%;
  height: 36px;
  padding: 0 2px;
}

.ls-review--tipping-other-hovered,
.ls-review--tipping-no-tip-hovered {
  background-color: $primary;
  color: $white;
}

.themed-app {
  .ls-review--tipping {
    .button {
      background-color: $theme-color-primary-light;
      color: $theme-color-primary;
      &:hover {
        background-color: $theme-color-primary;
        color: $theme-color-white;
      }
      &.is-primary {
        background-color: $theme-color-primary;
        color: $theme-color-white;
      }
    }
  }

  .ls-review--tipping-percentage-text {
    font-size: $size-3;
  }

  .ls-review--tipping-other,
  .ls-review--tipping-no-tip {
    background-color: $theme-color-primary-light;
    color: $theme-color-primary;
    font-size: $theme-txt-body-lg;
  }

  .ls-review--tipping-other-hovered,
  .ls-review--tipping-no-tip-hovered {
    background-color: $theme-color-primary;
    color: $theme-color-white;
  }
}
</style>
